import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, AUTH_EXPIRED_EVENT, AUTH_TOKEN_KEY } from '@/lib/apiClient';

export type UserRole = 'admin' | 'veterinarian' | 'seller';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  mustChangePassword: boolean;
}

interface AuthSessionResponse {
  accessToken: string;
  user: AuthUser;
  expiresIn: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  checking: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);

  const clearSession = useCallback(() => {
    window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
  }, []);

  useEffect(() => {
    const token = window.sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    api.get<AuthUser>('/auth/me')
      .then(setUser)
      .catch(clearSession)
      .finally(() => setChecking(false));
  }, [clearSession]);

  useEffect(() => {
    window.addEventListener(AUTH_EXPIRED_EVENT, clearSession);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, clearSession);
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post<AuthSessionResponse>('/auth/login', { email, password });
    window.sessionStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
    setUser(response.user);
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      // El cierre local siempre debe completarse aunque se haya perdido conexión.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const response = await api.post<AuthSessionResponse>('/auth/change-password', { currentPassword, newPassword });
    window.sessionStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
    setUser(response.user);
  }, []);

  const value = useMemo(() => ({ user, checking, login, logout, changePassword }), [user, checking, login, logout, changePassword]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe utilizarse dentro de AuthProvider');
  return context;
}
